import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useRef } from 'react';

import { CSSTransition } from 'react-transition-group';

import { FilestackPreviewImage } from '@components/common/filestack_uploader/FilestackPreviewImage';
import { FilestackUploader } from '@components/common/filestack_uploader/FilestackUploader';
import { UppyTusWrapper } from '@components/common/uppy/uppy_ucs/UppyTusWrapper';
import { TextWarningButton } from '@components/library/button';
import { MoreInfoTooltip } from '@components/library/tooltip';
import { isUppyWithCompanionEnabled } from '@helpers/uppy';

import '../styles/CustomLogoUpload.scss';

export interface CustomLogoUploadProps {
  handleUpdate: (url: string | null) => void;
  isGalleryViewOpen: boolean;
  customLogoUrl: string | null;
}

const customLogoUploadTransitionTime = 300;

export const CustomLogoUpload: FunctionComponent<CustomLogoUploadProps> = ({
  handleUpdate,
  isGalleryViewOpen,
  customLogoUrl = null,
}) => {
  const triggerClickHandlerRef = useRef<((event: React.MouseEvent) => void) | null>(null);
  const uploadAreaId = 'custom-logo-upload';
  return (
    <CSSTransition
      in={isGalleryViewOpen}
      timeout={customLogoUploadTransitionTime}
      unmountOnExit
    >
      <div className="custom-logo-upload-container">
        <h5 className="logo-label">
          <Trans>Logo</Trans>
          <MoreInfoTooltip
            className='custom-logo-upload-tooltip'
            iconSize={13}
            id="custom-logo-upload-tooltip"
            tooltip={t`Upload a logo for a branded Share Link experience`}
          />
        </h5>
        <p id="logo-recommendation"><Trans>Recommended size: 128x128px, PNG, JPG.</Trans></p>
        {customLogoUrl ? (
          <div className="custom-logo-upload__filestack-container--thumbnail">
            <div className="custom-logo-upload__filestack-container--thumbnail-container">
              <FilestackPreviewImage
                alt="Header"
                className="filestack-container--thumbnail--image"
                data-private
                src={customLogoUrl}
              />
            </div>
            <TextWarningButton
              className="portal-delete-button stage-3-delete"
              icon="bff-trash"
              onClick={(): void => handleUpdate(null)}
            >
              <Trans>Delete Image</Trans>
            </TextWarningButton>
          </div>
        ) : (
          isUppyWithCompanionEnabled() ? (
            <>
              <div className="filestack-uploader-component">
                <div className="filestack-upload-area-wrapper">
                  <div
                    className="filestack-upload-area companion-upload-trigger"
                    id={uploadAreaId}
                    onClick={(e): void => { if (triggerClickHandlerRef.current) triggerClickHandlerRef.current(e); }}
                    onKeyUp={(e): void => {
                      if (triggerClickHandlerRef.current) {
                        triggerClickHandlerRef.current(e as unknown as React.MouseEvent);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="fsp-drop-pane__container">
                      <div className="fsp-drop-pane__text">
                        <Trans>Drag file(s) here or click to browse</Trans>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <UppyTusWrapper
                button
                handleUpload={(files): void => {
                  if (!files.length) return;
                  handleUpdate(files[0].url);
                }}
                onTriggerClickHandler={triggerClickHandlerRef}
                restrictions={{ maxNumberOfFiles: 1, allowedFileTypes: ['image/*'] }}
                trigger={`#${uploadAreaId}`}
                uniqIdentifier="uppy-custom-logo"
              />
            </>
          ) : (
            <FilestackUploader
              onUploadDone={(files): void => {
                const uploadUrl = files.filesUploaded[0].url;
                handleUpdate(uploadUrl);
              }}
              pickerOptions={{
                accept: 'image/*',
                maxFiles: 1,
              }}
            />
          )
        )}
      </div>
    </CSSTransition>
  );
};
